/**
 * The ledger cut-over (TMMPDM-0095, TMMAPI-0162 §I items 26–30) on Product settings › Billing
 * maintenance, in its fixed order: the readiness read, the pay-api A2 confirmation, freeze, the live
 * verticals confirmation, the opening adjustments (a preview, then the post), and the ledger mode.
 *
 * The server decides everything here: each check is shown as sent, each step's button is enabled by
 * that step's own `allowed`, and Post opening adjustments by the preview's own `postable`. The console
 * shows no "ready" verdict of its own. Each step is an audited tmm-api action behind a confirm whose
 * errors stay inside it (TMM-B4). A 404 on the readiness read means tmm-api hasn't deployed the
 * cut-over: the panel says so and shows no steps. Going `LIVE` is the Operator's environment change.
 *
 * TMMPDM-0096: `OPENING_COMPUTED` failures are a table (the server's
 * `"<payeeRef> <KIND> <sourceId> <amount>"` lines). TMMPDM-0097 formats an integer amount
 * token as signed minor units in the preview partner's currency. A 409 `LEDGER_CUTOVER_NOT_READY` lists
 * `details.failedChecks` in the confirm and re-reads readiness without closing it.
 * TMMPDM-0098: a no-amount line (`MISMATCH`, `NO_PAYEE`, `OTHER_CURRENCY`, `TENANT_WITHOUT_RESELLER`)
 * is the same table with Source and Amount absent. An affiliate row links through the preview
 * partner's `resellerId`. `TENANT_WITHOUT_RESELLER` stays the raw payee ref, unlinked.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, ErrorState, SectionCard, SubmitButton, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LedgerMoney } from '../ledger/ledgerShared';
import { StatusChip } from '@/status';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy } from '@/api/errors';
import { NOT_SENT, formatInstant, formatNumber } from '@/format';
import type {
  LedgerCutoverCheck,
  LedgerCutoverStep,
  LedgerCutoverView,
  LedgerMode,
  LedgerOpeningRow,
  LedgerOpeningView,
} from '@/api/tmm/types';
import {
  isNotDeployed,
  ledgerModeLabel,
  periodLabel,
  resellerLedgerPath,
  type Translate,
} from '../ledger/ledger';

type StepKey = 'A2_CONFIRMATION' | 'FREEZE' | 'LIVE_VERTICALS_CONFIRMATION';

type Readiness =
  | { state: 'loading' }
  | { state: 'ready'; view: LedgerCutoverView }
  | { state: 'notDeployed' }
  | { state: 'failed'; failure: unknown };

const CHECK_FALLBACK: Record<string, string> = {
  A2_CONFIRMED: 'pay-api A2 confirmed live in production',
  OLD_STATEMENTS_SETTLED: 'The last old statement run and paid',
  PAYEES_READY: 'Every partner set up as a payee',
  OPENING_COMPUTED: 'Opening adjustments computed',
  DEACTIVATION_WINDOWS_RESOLVED: 'Deactivation windows resolved',
};

/** A check in words; one the console doesn't know is shown by its code. */
function checkLabel(check: string, t: Translate): string {
  return Object.hasOwn(CHECK_FALLBACK, check)
    ? t(`ledger.cutover.check.${check}`, CHECK_FALLBACK[check])
    : check;
}

const FAILURE_KIND_FALLBACK: Record<string, string> = {
  REFUND_OF_COVERED_SALE: 'A refund of a sale the opening already counts.',
  CLAWBACK: 'A commission clawback.',
  RECOVERY: 'A recovery.',
  UNPOSTED_PAYMENT: 'A payment not posted yet.',
  UNPOSTED_COMMISSION: 'A commission not posted yet.',
  UNPOSTED_SHORTFALL: 'A minimum shortfall not posted yet.',
  NETTING_ADDON: 'A netted add-on.',
  SENT_BEFORE: 'Sent before the cut-over.',
  MISMATCH: "The reconciliation doesn't match.",
  NO_PAYEE: 'No payee is set up for this partner.',
  OTHER_CURRENCY: 'A line in another currency.',
  TENANT_WITHOUT_RESELLER:
    "This partner's tenant has no reseller yet. Fix the tenant, then run the preview again.",
};

/** Two-token opening failures. Anything else with two tokens stays a raw line. */
const NO_AMOUNT_KINDS = new Set([
  'MISMATCH',
  'NO_PAYEE',
  'OTHER_CURRENCY',
  'TENANT_WITHOUT_RESELLER',
]);

/** A failure kind in words; one the console doesn't know is shown by its code. */
function failureKindLabel(kind: string, t: Translate): string {
  return Object.hasOwn(FAILURE_KIND_FALLBACK, kind)
    ? t(`ledger.cutover.failureKind.${kind}`, FAILURE_KIND_FALLBACK[kind])
    : kind;
}

const PAYEE_REF = /^(RESELLER|AFFILIATE):(\d+)$/;

/** One parsed `OPENING_COMPUTED` failure. `sourceId` and `amount` are null on a no-amount line. */
interface ParsedOpeningFailure {
  payeeRef: string;
  kind: 'RESELLER' | 'AFFILIATE';
  id: number;
  failureKind: string;
  sourceId: string | null;
  amount: string | null;
}

function parsePayee(payeeRef: string): { kind: 'RESELLER' | 'AFFILIATE'; id: number } | null {
  const match = PAYEE_REF.exec(payeeRef);
  const kind = match?.[1];
  const idText = match?.[2];
  if ((kind !== 'RESELLER' && kind !== 'AFFILIATE') || idText === undefined) return null;
  const id = Number(idText);
  if (!Number.isSafeInteger(id)) return null;
  return { kind, id };
}

/**
 * Four tokens `"<payeeRef> <KIND> <sourceId> <amount>"`, or two tokens `"<payeeRef> <KIND>"` when
 * KIND is a no-amount form. Anything else is shown raw, never dropped.
 */
function parseOpeningFailure(entry: string): ParsedOpeningFailure | null {
  const tokens = entry.trim().split(/\s+/);
  const payeeRef = tokens[0];
  const failureKind = tokens[1];
  if (payeeRef === undefined || failureKind === undefined) return null;
  const payee = parsePayee(payeeRef);
  if (payee === null) return null;
  if (tokens.length === 2) {
    if (!NO_AMOUNT_KINDS.has(failureKind)) return null;
    return { payeeRef, ...payee, failureKind, sourceId: null, amount: null };
  }
  if (tokens.length !== 4) return null;
  const sourceId = tokens[2];
  const amount = tokens[3];
  if (sourceId === undefined || amount === undefined) return null;
  return { payeeRef, ...payee, failureKind, sourceId, amount };
}

/**
 * Reseller rows use the id in the payee ref. Affiliate rows use the preview partner's reseller id.
 * `TENANT_WITHOUT_RESELLER` is never linked. An affiliate with no preview row yet has no reseller id.
 */
function openingFailureHref(
  parsed: ParsedOpeningFailure,
  row: LedgerOpeningRow | undefined,
): string | null {
  if (parsed.failureKind === 'TENANT_WITHOUT_RESELLER') return null;
  if (parsed.kind === 'RESELLER') return resellerLedgerPath(parsed.id);
  if (row === undefined) return null;
  return `/partners/${row.resellerId}/affiliates/${parsed.id}?tab=ledger`;
}

/** 409 `LEDGER_CUTOVER_NOT_READY`: the check names, or none when `failedChecks` isn't a string array. */
function isCutoverNotReady(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    String(error.code) === 'LEDGER_CUTOVER_NOT_READY'
  );
}

function failedCheckNames(error: ApiError): string[] {
  const raw = error.details.backend?.['failedChecks'];
  if (!Array.isArray(raw) || !raw.every((item) => typeof item === 'string')) return [];
  return raw;
}

/** The confirm's refusal: the not-ready list, or a catalog sentence. Never the server's message. */
function cutoverConfirmError(
  failure: unknown,
  t: Translate,
  fallback: string,
): { notReady: string[] | null; message: string | null } {
  if (isNotDeployed(failure)) {
    return {
      notReady: null,
      message: t('ledger.cutover.notAvailable', "The ledger cut-over isn't available yet."),
    };
  }
  if (isCutoverNotReady(failure)) return { notReady: failedCheckNames(failure), message: null };
  return { notReady: null, message: errorCopy(failure, t, fallback) };
}

function NotReadyChecks({ checks }: { checks: readonly string[] }) {
  const { t } = useCopy();
  return (
    <Box>
      <Typography variant="body2" component="p">
        {t('ledger.cutover.notReadyYet', 'Not ready yet:')}
      </Typography>
      {checks.length > 0 ? (
        <Box component="ul" sx={{ my: 0.5, pl: 2 }}>
          {checks.map((name, index) => (
            <Box component="li" key={`${name}-${index}`}>
              {checkLabel(name, t)}
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export function LedgerCutoverSection({ mode }: { mode: LedgerMode | null }) {
  const { t } = useCopy();
  const notify = useNotify();
  const [token, setToken] = useState(0);
  const [readiness, setReadiness] = useState<{ token: number; value: Readiness } | null>(null);
  const reload = useCallback(() => setToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getLedgerCutover()
      .then((view) => {
        if (!cancelled) setReadiness({ token, value: { state: 'ready', view } });
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        setReadiness({
          token,
          value: isNotDeployed(failure) ? { state: 'notDeployed' } : { state: 'failed', failure },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // While a re-read is in flight the last answer stays on screen.
  const current = readiness?.value ?? { state: 'loading' };

  return (
    <SectionCard title={t('ledger.cutover.title', 'Ledger cut-over')} id="ledger-cutover-title">
      {current.state === 'loading' ? <LoadingSpinner /> : null}
      {current.state === 'notDeployed' ? (
        <Typography variant="body1" color="text.secondary">
          {t('ledger.cutover.notAvailable', "The ledger cut-over isn't available yet.")}
        </Typography>
      ) : null}
      {current.state === 'failed' ? (
        <ErrorState
          error={current.failure}
          message={t('ledger.cutover.loadError', "Couldn't load the cut-over readiness.")}
          onRetry={reload}
        />
      ) : null}
      {current.state === 'ready' ? (
        <CutoverSteps
          view={current.view}
          onStepDone={(message) => {
            notify.success(message);
            reload();
          }}
          onRefused={reload}
        />
      ) : null}
      {mode !== null ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1">
            {t('ledger.cutover.mode', 'Ledger mode: {{mode}}', { mode: ledgerModeLabel(mode, t) })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'ledger.cutover.modeHelp',
              "Going LIVE is the Operator's environment change on both tmm-api wars.",
            )}
          </Typography>
        </Box>
      ) : null}
    </SectionCard>
  );
}

function CutoverSteps({
  view,
  onStepDone,
  onRefused,
}: {
  view: LedgerCutoverView;
  onStepDone: (message: string) => void;
  onRefused: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [preview, setPreview] = useState<LedgerOpeningView | null>(null);
  const checks = view.checks;
  const steps = view.steps;
  const outbox = view.pendingOutbox;
  const partners = preview ? preview.partners : [];
  const stepOf = (key: string): LedgerCutoverStep | undefined => steps.find((s) => s.step === key);

  return (
    <Box component="ol" sx={{ pl: 3, m: 0, '& > li': { mb: 3 } }}>
      <li>
        <Typography variant="subtitle1" component="h4" id="ledger-cutover-readiness">
          {t('ledger.cutover.readiness', 'Readiness')}
        </Typography>
        <Box
          component="ul"
          aria-labelledby="ledger-cutover-readiness"
          sx={{ pl: 0, listStyle: 'none' }}
        >
          {checks.map((check, index) => (
            <CheckRow key={`${check.check}-${index}`} check={check} partners={partners} />
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {outbox.length === 0
            ? t('ledger.cutover.outboxNone', 'No pending outbox rows.')
            : t('ledger.cutover.outbox', 'Pending outbox rows: {{periods}}', {
                periods: outbox
                  .map(
                    (period) =>
                      `${periodLabel(period.periodMonth, locale)}: ${formatNumber(period.rows, locale)}`,
                  )
                  .join(' · '),
              })}
        </Typography>
      </li>
      <li>
        <StepAction
          step={stepOf('A2_CONFIRMATION')}
          stepKey="A2_CONFIRMATION"
          action={t('ledger.cutover.a2.action', 'Confirm pay-api A2 is live in production…')}
          recorded={(when) =>
            t('ledger.cutover.a2.recorded', 'pay-api A2 confirmed {{when}}', { when })
          }
          title={t('ledger.cutover.a2.title', 'Confirm pay-api A2 is live in production?')}
          consequence={t(
            'ledger.cutover.a2.words',
            "Confirm only once you've checked that pay-api's A2 release is live in production. It's audited.",
          )}
          confirmLabel={t('ledger.cutover.a2.confirm', 'Confirm A2')}
          failed={t('ledger.cutover.a2.failed', "Couldn't record the confirmation.")}
          run={() => adminApi.confirmLedgerA2()}
          onDone={() => onStepDone(t('ledger.cutover.a2.done', 'pay-api A2 confirmed.'))}
          onRefused={onRefused}
        />
      </li>
      <li>
        <StepAction
          step={stepOf('FREEZE')}
          stepKey="FREEZE"
          destructive
          action={t('ledger.cutover.freeze.action', "Freeze tmm's payout machinery…")}
          recorded={(when) =>
            t('ledger.cutover.freeze.recorded', 'Payout machinery frozen {{when}}', { when })
          }
          title={t('ledger.cutover.freeze.title', "Freeze tmm's payout machinery?")}
          consequence={t(
            'ledger.cutover.freeze.words',
            'Transfers, the payout close and run, and deficit and shortfall charging stop, with their retries. Nothing is deleted. Running it again changes nothing.',
          )}
          confirmLabel={t('ledger.cutover.freeze.confirm', 'Freeze payouts')}
          failed={t('ledger.cutover.freeze.failed', "Couldn't freeze the payout machinery.")}
          run={() => adminApi.freezeLedgerPayouts()}
          onDone={() => onStepDone(t('ledger.cutover.freeze.done', 'Payout machinery frozen.'))}
          onRefused={onRefused}
        />
      </li>
      <li>
        <StepAction
          step={stepOf('LIVE_VERTICALS_CONFIRMATION')}
          stepKey="LIVE_VERTICALS_CONFIRMATION"
          action={t(
            'ledger.cutover.verticals.action',
            "Confirm tmm-api is in pay-api's live verticals…",
          )}
          recorded={(when) =>
            t('ledger.cutover.verticals.recorded', 'Live verticals confirmed {{when}}', { when })
          }
          title={t(
            'ledger.cutover.verticals.title',
            "Confirm tmm-api is in pay-api's live verticals?",
          )}
          consequence={t(
            'ledger.cutover.verticals.words',
            "Confirm only once tmm-api is in PAYAPI_SETTLEMENT_LIVE_VERTICALS on both pay-api wars. tmm-api can't read that setting. It's audited.",
          )}
          confirmLabel={t('ledger.cutover.verticals.confirm', 'Confirm live verticals')}
          failed={t('ledger.cutover.verticals.failed', "Couldn't record the confirmation.")}
          run={() => adminApi.confirmLedgerLiveVerticals()}
          onDone={() => onStepDone(t('ledger.cutover.verticals.done', 'Live verticals confirmed.'))}
          onRefused={onRefused}
        />
      </li>
      <li>
        <OpeningStep
          step={stepOf('OPENING')}
          preview={preview}
          onPreview={setPreview}
          onPosted={onStepDone}
          onRefused={onRefused}
        />
      </li>
    </Box>
  );
}

function CheckRow({
  check,
  partners,
}: {
  check: LedgerCutoverCheck;
  partners: readonly LedgerOpeningRow[];
}) {
  const { t } = useCopy();
  const failures = check.failures;
  const stateLabel = check.passed
    ? t('ledger.cutover.passed', 'Passed')
    : t('ledger.cutover.failing', 'Failing');
  return (
    <Box component="li" sx={{ mb: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <StatusChip tone={check.passed ? 'success' : 'warning'} label={stateLabel} />
        <Typography variant="body2" component="span">
          {check.check !== '' ? checkLabel(check.check, t) : NOT_SENT}
        </Typography>
      </Stack>
      {check.check === 'DEACTIVATION_WINDOWS_RESOLVED' ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {t(
            'ledger.cutover.checkHint.DEACTIVATION_WINDOWS_RESOLVED',
            "Ledger rows inside a reseller's deactivation window have been rebuilt.",
          )}
        </Typography>
      ) : null}
      {check.check === 'OPENING_COMPUTED' ? (
        <OpeningFailures entries={failures} partners={partners} />
      ) : failures.length > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {t('ledger.cutover.failures', 'Not ready: {{names}}', { names: failures.join(', ') })}
        </Typography>
      ) : null}
    </Box>
  );
}

function previewRow(
  partners: readonly LedgerOpeningRow[],
  payeeRef: string,
): LedgerOpeningRow | undefined {
  return partners.find((row) => row.payeeRef === payeeRef);
}

/** The preview's partner name when this section has already loaded one; otherwise the raw ref. */
function partnerLabel(row: LedgerOpeningRow | undefined, payeeRef: string): string {
  const name = row?.partnerName;
  return typeof name === 'string' && name.trim() !== '' ? name : payeeRef;
}

/** The preview row's currency code, when this section has loaded one. An empty code is absent. */
function partnerCurrency(row: LedgerOpeningRow | undefined): string | undefined {
  if (row === undefined || row.currency === '') return undefined;
  return row.currency;
}

/**
 * The failure's fourth token as signed minor units, or null when it is not exactly a safe
 * integer (optional leading `-`, digits only, no leading zeros). `String(minor) === token`
 * rejects a digit string `Number` would round. Not run through `exactMinorFromMajor`.
 */
function openingMinor(token: string): number | null {
  if (!/^-?\d+$/.test(token)) return null;
  const minor = Number(token);
  if (!Number.isSafeInteger(minor) || String(minor) !== token) return null;
  return minor;
}

/**
 * `OPENING_COMPUTED` failures only. Parsed lines are a table; a line that isn't four tokens, or
 * whose payee ref isn't `RESELLER:<digits>` / `AFFILIATE:<digits>`, stays one raw line.
 */
function OpeningFailures({
  entries,
  partners,
}: {
  entries: readonly string[];
  partners: readonly LedgerOpeningRow[];
}) {
  const { t } = useCopy();
  if (entries.length === 0) return null;
  const segments: Array<
    | { kind: 'raw'; text: string }
    | { kind: 'rows'; rows: Array<{ entry: string; parsed: ParsedOpeningFailure }> }
  > = [];
  for (const entry of entries) {
    const parsed = parseOpeningFailure(entry);
    const last = segments[segments.length - 1];
    if (parsed === null) segments.push({ kind: 'raw', text: entry });
    else if (last?.kind === 'rows') last.rows.push({ entry, parsed });
    else segments.push({ kind: 'rows', rows: [{ entry, parsed }] });
  }
  const firstTable = segments.findIndex((segment) => segment.kind === 'rows');
  return (
    <Box sx={{ ml: 1, mt: 0.5 }}>
      {segments.map((segment, index) =>
        segment.kind === 'raw' ? (
          <Typography key={`raw-${index}`} variant="body2" color="text.secondary">
            {segment.text}
          </Typography>
        ) : (
          <Box key={`rows-${index}`}>
            {index === firstTable ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t(
                  'ledger.cutover.openingFailures.intro',
                  'For each row, post an adjustment for that partner, then run the preview again.',
                )}
              </Typography>
            ) : null}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table
                size="small"
                aria-label={t('ledger.cutover.openingFailures.table', 'Opening check failures')}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('ledger.cutover.openingFailures.col.partner', 'Partner')}
                    </TableCell>
                    <TableCell>{t('ledger.cutover.openingFailures.col.what', 'What')}</TableCell>
                    <TableCell>
                      {t('ledger.cutover.openingFailures.col.source', 'Source')}
                    </TableCell>
                    <TableCell align="right">
                      {t('ledger.cutover.openingFailures.col.amount', 'Amount')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {segment.rows.map(({ entry, parsed }, rowIndex) => {
                    const row = previewRow(partners, parsed.payeeRef);
                    const unlinked = parsed.failureKind === 'TENANT_WITHOUT_RESELLER';
                    const label = unlinked ? parsed.payeeRef : partnerLabel(row, parsed.payeeRef);
                    const href = openingFailureHref(parsed, row);
                    const currency = partnerCurrency(row);
                    const minor = parsed.amount === null ? null : openingMinor(parsed.amount);
                    return (
                      <TableRow key={`${entry}-${rowIndex}`}>
                        <TableCell>
                          {href !== null ? (
                            <Link component={RouterLink} to={href}>
                              {label}
                            </Link>
                          ) : (
                            label
                          )}
                        </TableCell>
                        <TableCell>{failureKindLabel(parsed.failureKind, t)}</TableCell>
                        <TableCell>{parsed.sourceId ?? NOT_SENT}</TableCell>
                        <TableCell align="right">
                          {parsed.amount === null ? (
                            NOT_SENT
                          ) : currency !== undefined && minor !== null ? (
                            <LedgerMoney minor={minor} currency={currency} />
                          ) : (
                            parsed.amount
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ),
      )}
    </Box>
  );
}

interface StepActionProps {
  step: LedgerCutoverStep | undefined;
  stepKey: StepKey;
  destructive?: boolean;
  action: string;
  /** The recorded step in words, with when (empty when the server sent no date). */
  recorded: (when: string) => string;
  title: string;
  consequence: string;
  confirmLabel: string;
  failed: string;
  run: () => Promise<void>;
  onDone: () => void;
  /** A 409 `LEDGER_CUTOVER_NOT_READY`: re-read readiness while this confirm stays open. */
  onRefused: () => void;
}

/** One audited step: done (with its date), or its button, enabled by the server's `allowed`. */
function StepAction({
  step,
  stepKey,
  destructive = false,
  action,
  recorded,
  title,
  consequence,
  confirmLabel,
  failed,
  run,
  onDone,
  onRefused,
}: StepActionProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState<string[] | null>(null);

  if (step?.recorded) {
    return (
      <Typography variant="body1" data-step={stepKey}>
        {recorded(step.recordedAt ? formatInstant(step.recordedAt, i18n.language) : '')}
      </Typography>
    );
  }

  const confirm = async () => {
    setBusy(true);
    setError(null);
    setNotReady(null);
    try {
      await run();
      setOpen(false);
      onDone();
    } catch (failure: unknown) {
      const shown = cutoverConfirmError(failure, t, failed);
      setNotReady(shown.notReady);
      setError(shown.message);
      if (shown.notReady !== null) onRefused();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color={destructive ? 'error' : 'primary'}
        disabled={step?.allowed !== true}
        onClick={() => {
          setError(null);
          setNotReady(null);
          setOpen(true);
        }}
      >
        {action}
      </Button>
      {step?.allowed !== true ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('ledger.cutover.refused', 'Not yet: the readiness above says what is missing.')}
        </Typography>
      ) : null}
      <ConfirmDialog
        open={open}
        title={title}
        consequence={consequence}
        confirmLabel={confirmLabel}
        destructive={destructive}
        busy={busy}
        error={notReady !== null ? <NotReadyChecks checks={notReady} /> : error}
        onConfirm={() => void confirm()}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}

/** Opening adjustments: a dry-run preview, then the post the preview's own `postable` allows. */
function OpeningStep({
  step,
  preview,
  onPreview,
  onPosted,
  onRefused,
}: {
  step: LedgerCutoverStep | undefined;
  preview: LedgerOpeningView | null;
  onPreview: (preview: LedgerOpeningView | null) => void;
  onPosted: (message: string) => void;
  onRefused: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState<string[] | null>(null);

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      onPreview(await adminApi.ledgerOpening(false));
    } catch (failure: unknown) {
      onPreview(null);
      setPreviewError(
        isNotDeployed(failure)
          ? t('ledger.cutover.notAvailable', "The ledger cut-over isn't available yet.")
          : errorCopy(
              failure,
              t,
              t(
                'ledger.cutover.opening.previewFailed',
                "Couldn't preview the opening adjustments.",
              ),
            ),
      );
    } finally {
      setPreviewing(false);
    }
  };

  const post = async () => {
    setBusy(true);
    setError(null);
    setNotReady(null);
    try {
      onPreview(await adminApi.ledgerOpening(true));
      setOpen(false);
      onPosted(t('ledger.cutover.opening.done', 'Opening adjustments posted.'));
    } catch (failure: unknown) {
      const shown = cutoverConfirmError(
        failure,
        t,
        t('ledger.cutover.opening.failed', "Couldn't post the opening adjustments."),
      );
      setNotReady(shown.notReady);
      setError(shown.message);
      if (shown.notReady !== null) onRefused();
    } finally {
      setBusy(false);
    }
  };

  const rows = preview ? preview.partners : [];

  return (
    <>
      <Typography variant="subtitle1" component="h4">
        {t('ledger.cutover.opening.title', 'Opening adjustments')}
      </Typography>
      {step?.recorded ? (
        <Typography variant="body1">
          {t('ledger.cutover.opening.recorded', 'Opening adjustments posted {{when}}', {
            when: step.recordedAt ? formatInstant(step.recordedAt, i18n.language) : '',
          })}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t(
          'ledger.cutover.opening.help',
          "Each partner's opening is the balance carried on tmm's last paid statement. Preview it first; nothing is posted until you post it.",
        )}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <SubmitButton busy={previewing} variant="outlined" onClick={() => void runPreview()}>
          {t('ledger.cutover.opening.preview', 'Preview')}
        </SubmitButton>
        <Button
          variant="contained"
          disabled={preview?.postable !== true}
          onClick={() => {
            setError(null);
            setNotReady(null);
            setOpen(true);
          }}
        >
          {t('ledger.cutover.opening.post', 'Post opening adjustments…')}
        </Button>
      </Stack>
      {previewError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {previewError}
        </Alert>
      ) : null}
      {preview !== null ? <OpeningTable rows={rows} posted={preview.posted} /> : null}
      <ConfirmDialog
        open={open}
        title={t('ledger.cutover.opening.confirmTitle', 'Post the opening adjustments?')}
        consequence={t(
          'ledger.cutover.opening.confirmWords',
          "One opening adjustment is posted for each partner, at the figure tmm-api computes when you post; the preview shows those figures as of now. It's audited and posted once.",
        )}
        confirmLabel={t('ledger.cutover.opening.confirm', 'Post adjustments')}
        busy={busy}
        error={notReady !== null ? <NotReadyChecks checks={notReady} /> : error}
        onConfirm={() => void post()}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}

function OpeningTable({ rows, posted }: { rows: LedgerOpeningRow[]; posted: boolean }) {
  const { t } = useCopy();
  return (
    <>
      <Typography variant="body2" role="status" sx={{ mb: 1 }}>
        {posted
          ? t('ledger.cutover.opening.posted', 'Posted.')
          : t('ledger.cutover.opening.dryRun', 'Preview only: nothing has been posted.')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('ledger.cutover.opening.title', 'Opening adjustments')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('ledger.cutover.opening.col.partner', 'Partner')}</TableCell>
              <TableCell align="right">
                {t('ledger.cutover.opening.col.figure', 'Opening')}
              </TableCell>
              <TableCell>
                {t('ledger.cutover.opening.col.reconciliation', 'Reconciliation')}
              </TableCell>
              <TableCell align="right">
                {t('ledger.cutover.opening.col.expected', 'Old system')}
              </TableCell>
              <TableCell align="right">
                {t('ledger.cutover.opening.col.ledger', 'Ledger')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  {t('ledger.cutover.opening.none', 'No partner has an opening figure.')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const matches = row.reconciliation.matches;
                const matchLabel = matches
                  ? t('ledger.cutover.opening.matches', 'Reconciles')
                  : t('ledger.cutover.opening.mismatch', "Doesn't reconcile");
                const name =
                  typeof row.partnerName === 'string' && row.partnerName.trim() !== ''
                    ? row.partnerName
                    : row.payeeRef;
                return (
                  <TableRow
                    key={row.payeeRef}
                    sx={matches ? undefined : { '& > td': { fontWeight: 600 } }}
                  >
                    <TableCell>{name !== '' ? name : NOT_SENT}</TableCell>
                    <TableCell align="right">
                      <LedgerMoney minor={row.figureMinor} currency={row.currency} />
                    </TableCell>
                    <TableCell>
                      <StatusChip tone={matches ? 'success' : 'error'} label={matchLabel} />
                    </TableCell>
                    <TableCell align="right">
                      <LedgerMoney
                        minor={row.reconciliation.expectedMinor}
                        currency={row.currency}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <LedgerMoney minor={row.reconciliation.ledgerMinor} currency={row.currency} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
